/**
 * Cliente HTTP del Worker de Atreus.
 *
 * - URL base via `VITE_WORKER_URL` (env vite). Default `http://localhost:8787`.
 * - `apiFetch(path, init?)` adjunta Authorization Bearer si hay sesión y
 *   refresca transparentemente al recibir 401 — un sólo retry.
 * - `apiPost`/`apiGet`/`apiPatch` envuelven `apiFetch` con JSON.
 */

import {
  clearSession,
  loadSession,
  saveSession,
  type AdminSession,
} from './auth/session.js';

export const WORKER_URL =
  (import.meta.env['VITE_WORKER_URL'] as string | undefined) ??
  'http://localhost:8787';

interface RefreshResponse {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
}

const refresh = async (session: AdminSession): Promise<AdminSession> => {
  const res = await fetch(`${WORKER_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });
  if (!res.ok) throw new ApiError('refresh_failed', res.status);
  const body = (await res.json()) as RefreshResponse;
  const next: AdminSession = {
    ...session,
    accessToken: body.accessToken,
    accessExpiresAt: body.accessExpiresAt,
    refreshToken: body.refreshToken,
    savedAt: Date.now(),
  };
  saveSession(next);
  return next;
};

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    public detail?: unknown,
  ) {
    super(`api_error:${code} (${status})`);
  }
}

const sendOnce = async (
  path: string,
  init: RequestInit,
  token?: string,
): Promise<Response> => {
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(`${WORKER_URL}${path}`, { ...init, headers });
};

const parse = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const apiFetch = async <T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  let session = loadSession();
  // Refresh proactivo si el access expira pronto.
  if (session && session.accessExpiresAt < Date.now() + 30_000) {
    try {
      session = await refresh(session);
    } catch {
      clearSession();
      session = null;
    }
  }
  let res = await sendOnce(path, init, session?.accessToken);
  if (res.status === 401 && session) {
    try {
      session = await refresh(session);
      res = await sendOnce(path, init, session.accessToken);
    } catch {
      clearSession();
      throw new ApiError('unauthenticated', 401);
    }
  }
  const body = await parse(res);
  if (!res.ok) {
    const code =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'request_failed';
    throw new ApiError(code, res.status, body);
  }
  return body as T;
};

export const apiPost = <T>(path: string, body?: unknown): Promise<T> => {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiFetch<T>(path, init);
};
export const apiGet = <T>(path: string): Promise<T> =>
  apiFetch<T>(path, { method: 'GET' });
export const apiPatch = <T>(path: string, body?: unknown): Promise<T> => {
  const init: RequestInit = { method: 'PATCH' };
  if (body !== undefined) init.body = JSON.stringify(body);
  return apiFetch<T>(path, init);
};
export const apiDelete = <T>(path: string): Promise<T> =>
  apiFetch<T>(path, { method: 'DELETE' });
